// ServiceBoss search and share metadata (owner's SEO brief, 31 Oct 2026). One
// source for the prerenderers, the Functions, the middleware and the React
// pages, so the crawlable HTML and the hydrated page cannot disagree. Prices
// come from the catalogue: never write one here.
//
// ServiceBoss only. ServiceByReg and ServiceLad keep their own titles, share
// tags and viewport.

import { escapeHtml } from "./html.js";
import { packagesFor } from "../src/config/pricing.js";

const ORIGIN = "https://serviceboss.co.uk";
const [risk, complete] = packagesFor("serviceboss");

export const SERVICEBOSS_OG_IMAGE = `${ORIGIN}/og-image.png`;
export const SERVICEBOSS_ORG_ID = `${ORIGIN}/#org`;

export const serviceBossHomeMeta = Object.freeze({
  title: "Car Service History Check UK | ServiceBoss",
  description: `Check a car's manufacturer service history by registration. See service dates, mileage, garages and work done before you buy. Risk check £${risk.plate}, full report £${complete.plate}.`,
  ogDescription: "Check a car's manufacturer service history by registration. Service dates, mileage, garages and work done.",
  twitterDescription: "Check a car's manufacturer service history by registration.",
});

export const serviceBossPricingMeta = Object.freeze({
  title: `Car Check Prices: Risk Check £${risk.plate}, Full History £${complete.plate} | ServiceBoss`,
  description: `One-off vehicle checks, no subscription. ${risk.name} £${risk.plate} or ${complete.name} £${complete.plate} with manufacturer service records. Pay nothing if no records are found.`,
});

// ServiceBoss lets people pinch to zoom. The other two brands keep the zoom
// block the owner confirmed for them on 22 Oct 2026.
export function viewportContent(brandKey) {
  return brandKey === "serviceboss"
    ? "width=device-width, initial-scale=1"
    : "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no";
}

// iPhone Safari zooms in on any field under 16px when it is tapped, and with the
// zoom block gone this is what stops it. Keep it after the page's own CSS: the
// "font: inherit" resets in info.css and friends would otherwise undo it. Plate
// fields set a bigger size on a class, so they keep it.
export const SERVICEBOSS_FIELD_CSS = "input,select,textarea{font-size:16px}";

export function serviceBossTwitterTags({ title, description }) {
  return [
    '<meta name="twitter:card" content="summary_large_image">',
    `<meta name="twitter:title" content="${escapeHtml(title)}">`,
    `<meta name="twitter:description" content="${escapeHtml(description)}">`,
    `<meta name="twitter:image" content="${SERVICEBOSS_OG_IMAGE}">`,
  ];
}

export function serviceBossSocialTags({ title, description, canonical, type = "website", ogDescription = description, twitterDescription = description }) {
  return [
    `<meta property="og:type" content="${type}">`,
    '<meta property="og:site_name" content="ServiceBoss">',
    `<meta property="og:title" content="${escapeHtml(title)}">`,
    `<meta property="og:description" content="${escapeHtml(ogDescription)}">`,
    `<meta property="og:url" content="${escapeHtml(canonical)}">`,
    `<meta property="og:image" content="${SERVICEBOSS_OG_IMAGE}">`,
    '<meta property="og:image:width" content="1200">',
    '<meta property="og:image:height" content="630">',
    ...serviceBossTwitterTags({ title, description: twitterDescription }),
  ].map((tag) => `  ${tag}`).join("\n");
}

// The extra fields on the Organization every page shares. Same @id everywhere
// so search engines read one entity, not one per page.
export const serviceBossOrganizationFields = Object.freeze({
  "@id": SERVICEBOSS_ORG_ID,
  logo: SERVICEBOSS_OG_IMAGE,
  contactPoint: { "@type": "ContactPoint", contactType: "customer service", url: `${ORIGIN}/contact` },
});

export const serviceBossProductDescriptions = Object.freeze({
  risk: "Stolen marker, write-off category, outstanding finance, valuation, export/scrap history, mileage verification, VIC record.",
  complete: "Everything in the Risk Check plus manufacturer service history, dealer and garage trail and itemised service actions.",
});

// Article + FAQPage for one guide. The FAQ comes from the article's visible
// "Common questions" block, so the schema never says anything the page does not.
export function serviceBossGuideSchema(data, canonical) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: data.title,
        description: data.metaDescription,
        url: canonical,
        image: SERVICEBOSS_OG_IMAGE,
        datePublished: data.published,
        dateModified: data.updated,
        author: { "@type": "Organization", name: "ServiceBoss", url: `${ORIGIN}/` },
        publisher: { "@type": "Organization", "@id": SERVICEBOSS_ORG_ID, name: "ServiceBoss", url: `${ORIGIN}/` },
        mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      },
      ...(data.faq?.length
        ? [{
          "@type": "FAQPage",
          mainEntity: data.faq.map((item) => ({ "@type": "Question", name: item.q, acceptedAnswer: { "@type": "Answer", text: item.a } })),
        }]
        : []),
    ],
  };
}
